import { defineComponent as _defineComponent } from 'vue'
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'

  import HTMLRenderer from '@/components/common/HTMLRenderer.vue'
  import useCourse from '@/composables/useCourse'
  import useCommonMixin from '@/composables/useCommonMixin'
  import SVGRenderer from '@/components/common/SVGRenderer.vue'

  import { useRouter } from 'vue-router/composables'
  import useDuration from '@/composables/useDuration'
  import useIcons from '@/composables/useIcons'
  import useBreakpoint from '@/composables/useBreakpoint'
  import useI18n from '@/composables/useI18n'

  import { CourseModule } from '@/store/modules/course'

  import type { PropType } from 'vue'
  import type { CourseItem } from '@/services/interfaces/Course'

  import eventBus from '@/main'

  
export default /*#__PURE__*/_defineComponent({
  __name: 'CourseTile',
  props: {
    course: {
      type: Object as PropType<CourseItem>,
      required: true,
    },
    maxHeight: {
      type: Number,
      default: 0,
    },
  },
  emits: ['course-data'],
  setup(__props, { emit }) {

const props = __props;

  

  

  const router = useRouter()
  const { formatDurationToHHMM } = useDuration()
  const { guidedQuizDuration, courseGroupIds, productPrice, courseStateText } = useCourse()
  const { currencyFormatDE } = useCommonMixin()
  const { cardEmptyImage, openCourseIcon } = useIcons()
  const { isMobileDevice, isDesktopDevice, current } = useBreakpoint()
  const { translateString } = useI18n()

  const el = ref(null as HTMLDivElement | null)
  const courseTitleRef = ref(null as HTMLHeadingElement | null)

  const computedHeight = computed(() => (props.maxHeight ? props.maxHeight + 'px' : 'auto'))

  const courseDuration = computed(() => {
    const moduleDuration =
      CourseModule.coursesModuleMap[props.course.id]?.reduce((acc, mod) => {
        acc += guidedQuizDuration.value(mod.guidedQuiz?.numberOfQuestions || 0)
        return acc
      }, 0) || 0
    return formatDurationToHHMM((props.course.duration || 0) + moduleDuration)
  })

  const visibleGroupsLength = computed(() => courseGroupIds.value(props.course.id).length)

  const visibleForXGroups = computed(() =>
    translateString('trainings.visibleForXGroups', { groupsCount: visibleGroupsLength.value }),
  )

  const courseTags = props.course.tags.map((t) => t.title).join(', ')

  const routeToCourseInfoEditor = async () => {
    await CourseModule.setCourse(props.course)

    router.push({
      name: 'CourseInfoEditor',
      params: { courseId: props.course.id },
    })
  }

  const showAssignCourseGroupModal = () => {
    eventBus.$emit('show-modal', {
      modalContentComponent: 'AssignCourseGroupModal',
      modalProps: {
        course: props.course,
        groupIds: courseGroupIds.value(props.course.id),
      },
      cssClass: 'assign-course-group-modal',
      modalCloseCallback: (callback: () => void) => {
        if (typeof callback === 'function') callback()
      },
    })
  }

  const sendCourseData = () => {
    if (!courseTitleRef.value) return

    courseTitleRef.value.style.height = 'auto'

    nextTick(() => {
      const titleHeight = courseTitleRef.value?.offsetHeight || 0

      emit('course-data', {
        courseId: props.course.id,
        titleHeight,
        state: props.course.state,
      })

      courseTitleRef.value?.style.setProperty('height', computedHeight.value)
    })
  }

  let resizeTimeout: number | null = null
  const handleResize = () => {
    if (resizeTimeout) {
      clearTimeout(resizeTimeout)
    }
    resizeTimeout = window.setTimeout(() => {
      sendCourseData()
    }, 300)
  }

  watch(current, handleResize)

  onMounted(() => {
    setTimeout(() => {
      sendCourseData()
    }, 500)
  })

  onBeforeUnmount(() => {
    if (resizeTimeout) {
      clearTimeout(resizeTimeout)
    }

    emit('course-data', {
      courseId: props.course.id,
      titleHeight: 0,
      state: props.course.state,
    })
  })

return { __sfc: true,props, emit, router, formatDurationToHHMM, guidedQuizDuration, courseGroupIds, productPrice, courseStateText, currencyFormatDE, cardEmptyImage, openCourseIcon, isMobileDevice, isDesktopDevice, current, translateString, el, courseTitleRef, computedHeight, courseDuration, visibleGroupsLength, visibleForXGroups, courseTags, routeToCourseInfoEditor, showAssignCourseGroupModal, sendCourseData, resizeTimeout, handleResize, computed, nextTick, onBeforeUnmount, onMounted, ref, watch, HTMLRenderer, useCourse, useCommonMixin, SVGRenderer, useRouter, useDuration, useIcons, useBreakpoint, useI18n, CourseModule, eventBus }
}

})